import { useState } from 'react';

import {
  Button,
  Tooltip,
  Box
} from '@mui/material';

import Collapse from '@mui/material/Collapse';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import * as XLSX from 'xlsx-js-style';

import DownloadIcon from '@mui/icons-material/Download';
import { buildLineTableData } from '../utils/export/buildLineTableData';
import { buildTripTableData } from '../utils/export/buildTripTableData';
import formatWorksheet from '../utils/export/formatWorksheet';
import { m } from '../utils/dateUtils';
import { usePontosItinerarioStore } from '../stores/usePontosItinerarioStore';
import { useTripStatusAnalysisStore } from '../stores/analysis/useTripStatusAnalysisStore';
import { useTripPositionsAnalysisStore } from '../stores/analysis/useTripPositionsAnalysisStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { useLoadingStore, selectIsMainLoading } from '../stores/useLoadingStore';
import { useScreenStore } from '../stores/useScreenStore';
import { useLinesStore } from '../stores/useLinesStore';
import { useDriversStore, NO_DRIVER_ID} from '../stores/useDriversStore';
import { useTripOccurrencesStore } from '../stores/useTripOccurrencesStore';
import { useTripVehiclesChangesStore } from '../stores/useTripVehiclesChangesStore';
import { useTurnosStore } from '../stores/useTurnosStore';
import useHashListData from './hashs/useHashListData';
import { useCurrentHash } from '../hooks/hashs/useCurrentHash';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';
import { usePermissions } from '../hooks/auth/usePermissions';
import getKmlCodeFromPrefixo from '../utils/getKmlCodeFromPrefixo';

const EMPTY = [];

const ExportButton = () => {
    
    const perfisDisponiveis = useAuthStore(state => state.perfisDisponiveis);
    const isMainLoading = useLoadingStore(selectIsMainLoading);
    const monitorMode = useScreenStore(state => state.monitorMode);
    const { canExportType } = usePermissions();

    const [exportExpanded, setExportExpanded] = useState(false);
    
    const { sortedItems } = useHashListData();

    const {
        hashElemment,
        hashTitle
    } = useCurrentHash();

    const isOperationalMapMode = useOperationalMapStore(
        state => state.isOperationalMapMode
    );

    const operationalMapDateRange = useOperationalMapStore(
        state => state.operationalMapDateRange
    );

    // =========================
    // EXPORT DATA
    // =========================

    const exportItems = sortedItems;

    const hasItems = exportItems.length > 0;

    const exportFilename =
        isOperationalMapMode &&
        operationalMapDateRange
            ? `VIAGENS_${operationalMapDateRange.startDate.format('DD_MM_YYYY')}_A_${operationalMapDateRange.endDate.format('DD_MM_YYYY')}`
            : hashTitle || 'exportacao';

    const exportType = hashElemment;

    const showExportOptions = canExportType && exportType === 'TripCard';

    const exportTooltip = !hasItems
        ? 'Nenhum item para exportar'
        : exportType === 'TripCard'
            ? `Exportar ${exportItems.length === 1 ? 'a viagem' : `todas as ${exportItems.length} viagens`} em Excel`
            : `Exportar ${exportItems.length === 1 ? 'a linha' : `todas as ${exportItems.length} linhas`} em Excel`;


    const pontos = usePontosItinerarioStore(state => state.pontos);


    const isCompactMode = useLayoutStore(
        state => state.isCompactMode
    );

    const exportTrips = (tripExportType = 'escala') => {

        const driversDisponiveisMap =
            useDriversStore
                .getState()
                .driversDisponiveisMap;

        const getLastTripOccurrence =
            useTripOccurrencesStore
                .getState()
                .getLastTripOccurrence;

        const getLastTripVehicleChange =
            useTripVehiclesChangesStore
                .getState()
                .getLastTripVehicleChange;

        const turnosMap =
            useTurnosStore
                .getState()
                .turnosMap;

        const turnosCodesMap =
            useTurnosStore
                .getState()
                .turnosCodesMap;

        const metrosTotalRotaMap =
            useLinesStore
                .getState()
                .metrosTotalRotaMap;

        const rows = exportItems.map(trip => {

             const codigo = String(
                trip.CODIGO_INTEGRACAO
            ).trim();

            const pontosLinha = pontos[codigo] ?? EMPTY;

            const driverId = String(
                trip?.driver_id || NO_DRIVER_ID
            ).trim();

            const driver =
                driverId !== NO_DRIVER_ID
                    ? driversDisponiveisMap.get(driverId)
                    : null;

            const motorista = {
                nome:
                    driverId === NO_DRIVER_ID
                        ? 'Sem Motorista'
                        : driver?.NOMEFUNC || ' - ',

                matricula:
                    driverId === NO_DRIVER_ID
                        ? ' - '
                        : driverId
            };

            const ultimaOcorrencia = getLastTripOccurrence(trip.TRIP_UID);
            const ultimaTrocaVeic = getLastTripVehicleChange(trip.TRIP_UID);

            const turnoNome =
                tripExportType === 'escala'
                    ? getKmlCodeFromPrefixo(
                        trip?.PREFIXO,
                        turnosCodesMap
                    ) ?? ' - '
                    : turnosMap.get(
                        Number(trip?.turno) || 0
                    )?.nome ?? ' - ';

            const kmTotalRota = Math.round(
                (
                    metrosTotalRotaMap.get(codigo) ?? 0
                ) / 1000
            );

            const row =
                buildTripTableData({
                    trip,
                    tripStatus:
                        useTripStatusAnalysisStore
                            .getState()
                            .getStatus(
                                trip.DATA_REFERENCIA,
                                trip.CODIGO_INTEGRACAO
                            ),
                    positionsAnalysis:
                        useTripPositionsAnalysisStore
                            .getState()
                            .getPosition(
                                trip.DATA_REFERENCIA,
                                trip.CODIGO_INTEGRACAO
                            ),
                    pontos: pontosLinha,
                    perfisDisponiveis,
                    kmTotalRota,
                    motorista,
                    ultimaOcorrencia,
                    ultimaTrocaVeic,
                    turnoNome,
                    tripExportType,
                    forExcel: true
                });

            const {
                barColor,
                situacao,
                detalheSituacao,
                ...exportRow
            } = row;

            return exportRow;

        });

        const worksheet = formatWorksheet(
            XLSX.utils.json_to_sheet(rows),
            rows,
            {
                extraWidth: {
                    OS: 2
                }
            }
        );

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            'Viagens'
        );

        const filename =
            tripExportType === 'mapa_operacional'
                ? `${exportFilename}-MAPA-OPERACIONAL`
                : `${exportFilename}-ESCALA`;

        XLSX.writeFile(
            workbook,
            `${filename}-${m().format('DD.MM.YYYY')}.xlsx`
        );

    };

    const exportLines = () => {

        const turnosMap =
            useTurnosStore
                .getState()
                .turnosMap;

        const metrosTotalRotaMap =
            useLinesStore
                .getState()
                .metrosTotalRotaMap;

        const rows = exportItems.map(line => {

            const codigo = String(
                line?.CODIGO_INTEGRACAO || ''
            ).trim();

            const turnoNome = turnosMap.get(
                Number(line?.turno) || 0
            )?.nome ?? ' - ';

            const kmTotalRota = Math.round(
                (
                    metrosTotalRotaMap.get(codigo) ?? 0
                ) / 1000
            );

            return buildLineTableData({
                line,
                perfisDisponiveis,
                turnoNome,
                kmTotalRota
            });

        });

        const worksheet = formatWorksheet(
            XLSX.utils.json_to_sheet(rows),
            rows
        );

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            'Linhas'
        );

        XLSX.writeFile(
            workbook,
            `${exportFilename}-${m().format('DD.MM.YYYY')}.xlsx`
        );

    };



    const handleDownload = (tripExportType = null) => {

        if (!hasItems || isMainLoading) {
            return;
        }

        switch (exportType) {

            case 'LineCard':
                exportLines();
                break;

            case 'TripCard':

                if (canExportType && !tripExportType) {
                    setExportExpanded(prev => !prev);
                    return;
                }

                exportTrips(
                    canExportType
                        ? tripExportType
                        : 'escala'
                );

                setExportExpanded(false);
                break;

            default:
                return;

        }

    };



    return (
        <Box
            sx={{
                marginLeft: 'auto',
                marginRight: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1
            }}
        >
            <Tooltip title={exportTooltip}>
                <span>
                    <Button
                        color="success"
                        variant="contained"
                        disabled={!hasItems || isMainLoading}
                        onClick={() => handleDownload()}
                        size={
                            isCompactMode && monitorMode === 1
                                ? 'small'
                                : 'medium'
                        }
                        sx={{
                            minWidth: 'unset'
                        }}
                    >
                        <DownloadIcon
                            sx={{
                                fontSize:
                                    isCompactMode && monitorMode === 1
                                        ? '1.2rem'
                                        : '1.5rem'
                            }}
                        />

                        {(!isCompactMode || monitorMode === 2) &&
                            'Download'
                        }

                        {showExportOptions && (
                            <ChevronRightIcon
                                sx={{
                                    ml: 0.5,
                                    transform: exportExpanded
                                        ? 'rotate(180deg)'
                                        : 'rotate(0deg)',
                                    transition: 'transform 200ms'
                                }}
                            />
                        )}
                    </Button>
                </span>
            </Tooltip>

            {showExportOptions && (
                <Collapse
                    in={exportExpanded}
                    orientation="horizontal"
                    timeout={250}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            ml: 0.5,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            disabled={!hasItems || isMainLoading}
                            onClick={() => handleDownload('escala')}
                        >
                            Escala
                        </Button>

                        <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            disabled={!hasItems || isMainLoading}
                            onClick={() => handleDownload('mapa_operacional')}
                        >
                            Mapa Operacional
                        </Button>
                    </Box>
                </Collapse>
            )}
        </Box>
    );

};

export default ExportButton;